import { ref, readonly } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: number
  type: ToastType
  title: string
  message?: string
  actionLabel?: string
  onAction?: () => void
}

const MAX_VISIBLE = 3
// Auto-dismiss durations in ms
const DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  error: 6000,
  warning: 6000,
}

let nextId = 1
const toasts = ref<Toast[]>([])
const timers = new Map<number, ReturnType<typeof setTimeout>>()

export function pushToast(opts: {
  type: ToastType
  title: string
  message?: string
  actionLabel?: string
  onAction?: () => void
  duration?: number
}) {
  const id = nextId++
  const { duration, ...rest } = opts
  const toast: Toast = { id, ...rest }

  // Evict oldest when at capacity
  if (toasts.value.length >= MAX_VISIBLE) {
    const excess = toasts.value.splice(0, toasts.value.length - MAX_VISIBLE + 1)
    excess.forEach((t) => {
      const timer = timers.get(t.id)
      if (timer) {
        clearTimeout(timer)
        timers.delete(t.id)
      }
    })
  }

  toasts.value.push(toast)

  // Auto-dismiss timer (longer for action toasts if no explicit duration)
  const ms = duration ?? (toast.actionLabel ? 8000 : DURATIONS[toast.type])
  const timer = setTimeout(() => {
    dismissToast(id)
  }, ms)
  timers.set(id, timer)
}

export function dismissToast(id: number) {
  const timer = timers.get(id)
  if (timer) {
    clearTimeout(timer)
    timers.delete(id)
  }
  const idx = toasts.value.findIndex((t) => t.id === id)
  if (idx !== -1) toasts.value.splice(idx, 1)
}

export function useToast() {
  return {
    toasts: readonly(toasts),
    pushToast,
    dismissToast,
  }
}
